import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, App, Button, Layout, Space, Spin, Typography } from "antd";
import {
  idVerificationExpiresAt,
  idVerificationFreshness,
  type User,
  type VerifyEmailSendResponse,
} from "@gull/shared";
import { useAuth } from "../../lib/auth";
import { enduserApi, ApiError } from "../../lib/api";
import { useProfile } from "../../lib/useProfile";
import { BookingsTab } from "./BookingsTab";
import { DeliveriesTab } from "./DeliveriesTab";
import { ProfileTab } from "./ProfileTab";

const { Content } = Layout;
const { Title, Text } = Typography;

type MinSideSectionKey = "bookings" | "deliveries" | "profile";

const MIN_SIDE_NAV: Array<{ key: MinSideSectionKey; label: string }> = [
  { key: "bookings", label: "Bookinger" },
  { key: "deliveries", label: "Innleveringer" },
  { key: "profile", label: "Min profil" },
];

export default function MinSide() {
  const { user, loading, signInWithVipps, signOutUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get("seksjon");
  const requestedOfferCode = searchParams.get("kode")?.trim().toUpperCase() || null;
  const requestedOffer = searchParams.get("tilbud") === "1";
  const activeSection: MinSideSectionKey = isMinSideSectionKey(requestedSection)
    ? requestedSection
    : requestedOfferCode
      ? "deliveries"
      : "bookings";
  const profile = useProfile(user?.uid);

  const selectSection = (section: MinSideSectionKey) => {
    setSearchParams(section === "bookings" ? {} : { seksjon: section });
  };

  if (loading) {
    return (
      <Content style={{ padding: 48 }}>
        <Spin />
      </Content>
    );
  }
  if (!user) {
    return (
      <Content style={{ maxWidth: 460, margin: "72px auto", padding: 24, textAlign: "center" }}>
        <Title level={2} style={{ fontSize: 32, marginBottom: 24 }}>
          Logg inn for å se din side
        </Title>
        <Button className="vipps-button" type="primary" onClick={signInWithVipps}>
          Logg inn med Vipps
        </Button>
      </Content>
    );
  }

  const firstName =
    (profile?.name ?? user.displayName ?? user.email ?? "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)[0] || "kunde";
  const activeLabel = MIN_SIDE_NAV.find((item) => item.key === activeSection)?.label ?? "Min side";

  return (
    <Content className="minside-page">
      <div className="minside-shell">
        <aside className="minside-sidebar" aria-label="Min side meny">
          <div className="minside-sidebar__greeting">Hei, {firstName}!</div>
          <nav className="minside-nav">
            {MIN_SIDE_NAV.map((item) => (
              <button
                key={item.key}
                type="button"
                className={item.key === activeSection ? "is-active" : undefined}
                onClick={() => selectSection(item.key)}
              >
                {item.label}
              </button>
            ))}
          </nav>
          <button type="button" className="minside-signout" onClick={() => void signOutUser()}>
            Logg ut
          </button>
        </aside>

        <main className="minside-main">
          <EmailVerificationBanner profile={profile} />
          <IdRefreshBanner profile={profile} />
          <MinSideSection
            uid={user.uid}
            profile={profile}
            activeSection={activeSection}
            activeLabel={activeLabel}
            focusedOfferCode={requestedOffer ? requestedOfferCode : null}
          />
        </main>
      </div>
    </Content>
  );
}

function isMinSideSectionKey(value: string | null): value is MinSideSectionKey {
  return MIN_SIDE_NAV.some((item) => item.key === value);
}

function MinSideSection({
  uid,
  profile,
  activeSection,
  activeLabel,
  focusedOfferCode,
}: {
  uid: string;
  profile: User | null | undefined;
  activeSection: MinSideSectionKey;
  activeLabel: string;
  focusedOfferCode: string | null;
}) {
  const content = (() => {
    switch (activeSection) {
      case "bookings":
        return <BookingsTab uid={uid} />;
      case "deliveries":
        return <DeliveriesTab uid={uid} focusedOfferCode={focusedOfferCode} />;
      case "profile":
        return <ProfileTab profile={profile} />;
    }
  })();

  return (
    <section className={`minside-section minside-section--${activeSection}`}>
      <h1>{activeLabel}</h1>
      {content}
    </section>
  );
}

function IdRefreshBanner({ profile }: { profile: User | null | undefined }) {
  const { signInWithVipps } = useAuth();
  if (!profile) return null;
  const freshness = idVerificationFreshness(profile);
  if (freshness !== "expired" && freshness !== "expiring_soon") return null;
  const expiresAt = idVerificationExpiresAt(profile);
  const expiresLabel =
    expiresAt != null
      ? new Date(expiresAt).toLocaleDateString("nb-NO", {
          timeZone: "Europe/Oslo",
          dateStyle: "long",
        })
      : "snart";
  return (
    <Alert
      type={freshness === "expired" ? "error" : "warning"}
      showIcon
      style={{ marginBottom: 24 }}
      message={
        freshness === "expired" ? "ID-bekreftelsen er utløpt" : "ID-bekreftelsen utløper snart"
      }
      description={
        <Space direction="vertical" size={4}>
          <Text>
            {freshness === "expired"
              ? "Vi kan ikke utbetale før ID er bekreftet på nytt."
              : `Bekreftelsen utløper ${expiresLabel}.`}{" "}
            {profile.idVerificationMethod === "VIPPS"
              ? "Logg inn med Vipps på nytt for å fornye automatisk."
              : "Ta med pass / førerkort / nasjonalt ID-kort i butikk for å fornye."}
          </Text>
          {profile.idVerificationMethod === "VIPPS" || !profile.idVerificationMethod ? (
            <Button className="vipps-button" size="small" type="primary" onClick={signInWithVipps}>
              Bekreft på nytt med Vipps
            </Button>
          ) : null}
        </Space>
      }
    />
  );
}

function EmailVerificationBanner({ profile }: { profile: User | null | undefined }) {
  const { message } = App.useApp();
  const [sending, setSending] = useState(false);

  if (!profile) return null;
  if (!profile.email) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="Vi mangler e-postadressen din"
        description="Legg til en e-post for å motta kvitteringer og påminnelser. Kontakt butikken eller logg inn på nytt."
      />
    );
  }
  if (profile.emailVerified) return null;

  const sendLink = async () => {
    setSending(true);
    try {
      await enduserApi.authedPost<VerifyEmailSendResponse>("/me/email/verify-send", {});
      message.success("Bekreftelseslenke sendt på e-post");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Ukjent feil");
    } finally {
      setSending(false);
    }
  };

  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 24 }}
      message="Bekreft e-postadressen din"
      description={
        <Space direction="vertical">
          <Text>
            Vi har ikke bekreftet at <Text strong>{profile.email}</Text> tilhører deg.
          </Text>
          <Button size="small" loading={sending} onClick={() => void sendLink()}>
            Send bekreftelseslenke
          </Button>
        </Space>
      }
    />
  );
}
